<template>
  <v-alert
    v-if="showValidationError"
    icon="icon-warning-xs"
    color="red-faint"
    class="py-1 px-4 m-0 mb-6 text-primary"
  >
    <div class="body2 text-red-dark">
      {{ $t(`errors.fixErrorsBelow`) }}
    </div>
  </v-alert>
  <v-form ref="form" v-model="valid" @submit.prevent>
    <v-combobox
      v-model="localValue.names"
      v-model:search="currentInputValue"
      class="mt-3 coro-scrollable-combobox"
      :label="urlFieldLabel"
      :persistent-placeholder="false"
      :aria-label="urlFieldLabel"
      :rules="rules"
      :counter="50"
      hide-details="auto"
      variant="outlined"
      multiple
      chips
      no-filter
      hide-no-data
      validate-on="submit"
      @blur="handleBlur"
    >
      <template v-slot:message="{ message }">
        <div class="d-flex align-center">
          <v-icon icon="icon-warning-xs" color="red-faint" size="18" class="mr-1" />
          <span>{{ message }}</span>
        </div>
      </template>
      <template #chip="{ props, item }">
        <v-chip
          v-bind="props"
          variant="flat"
          :color="getChipColor(item.raw)"
          closable
          close-icon="$closeCircle"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="ml-1 mr-2" v-text="item.raw" />
          </div>
        </v-chip>
      </template>
    </v-combobox>
    <div class="mt-6">
      <div>
        <v-autocomplete
          v-model="localValue.labels"
          class="mt-4"
          :items="labels"
          :label="labelsFieldLabel"
          :persistent-placeholder="false"
          :disabled="actionNotAllowed"
          :rules="labelRules"
          variant="outlined"
          multiple
          return-object
          chips
          closable-chips
          clear-on-select
          data-testid="device-settings-tamper-resistance-labels"
          item-value="id"
          item-title="name"
          validate-on="submit"
        >
          <template v-slot:chip="{ props, item }">
            <v-chip
              v-bind="props"
              size="default"
              :color="item.raw.color"
              close-icon="$close"
              :text="item.raw.name"
              class="label-chip"
              variant="flat"
              label
              rounded="3"
            >
              <span class="coro-label text-uppercase text-primary mr-1" v-text="item.title"> </span>
            </v-chip>
          </template>
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props" title="">
              <div class="d-flex align-center label-popup-block">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span v-text="item.raw.name" />
              </div>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
    </div>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { hasSeparator, Patterns } from "@coronet/cui";
import uniq from "lodash/uniq";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module.ts";
import { storeToRefs } from "pinia";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import type { VuetifyFormRef } from "@/types.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import { useCustomDomainDnsStore } from "@/_store/network/swg/custom-domain-dns.module.ts";
import { useSWGStore } from "@/_store/network/swg/secure-web-gateway.module.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<{ allowed?: boolean }>>,
      required: true,
    },
  },
  emits: ["input", "update:valid", "update:localValue"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const devicesSettingsStore = useDevicesSettingsStore();
    const { labels } = storeToRefs(devicesSettingsStore);
    const customDomainDnsStore = useCustomDomainDnsStore();
    const { items: customDomainDnsItems } = storeToRefs(customDomainDnsStore);
    const { checkSwgUrlsUnique } = useSWGStore();

    const isAllowList = computed(() => !!props.config.item.allowed);

    const valid = ref(true);
    const form = ref<VuetifyFormRef>(null);
    const validationTriggered = ref(false);
    const localValue: Ref<{
      names: string[];
      allowed?: boolean;
      action: string;
      labels: DeviceLabel[];
    }> = ref({
      names: [],
      action: props.config.action ?? "",
      labels: [],
    });
    const currentInputValue = ref("");

    onMounted(async () => {
      localValue.value.allowed = props.config.item.allowed;
      await customDomainDnsStore.getItems();
    });

    watch(
      localValue,
      (newVal) => {
        // Reset validation when user makes changes after validation failed
        if (validationTriggered.value && showValidationError.value) {
          validationTriggered.value = false;
          showValidationError.value = false;
          // Clear field-level validation errors
          if (form.value) {
            form.value.resetValidation();
          }
        }
        const { names, allowed, labels } = newVal;
        emit("update:localValue", { names, allowed, labels });
      },
      { deep: true }
    );

    watch(currentInputValue, (newVal) => {
      if (!newVal) {
        return;
      }
      const hasCommaSeparator = hasSeparator(newVal);

      if (hasCommaSeparator) {
        handleCommaDelimiterPaste(newVal);
      }
    });

    const validateNamesFormat = (values: string[]): string | boolean => {
      if (!values || !values.length) return t("validations.required");
      if (values.length > 50) return t("validations.maximumNumberOfItemsExceeded");

      for (const value of values) {
        if (!matchWildcardDomain(value)) {
          return t("validations.url");
        }
      }
      return true;
    };

    const rules = [
      validateNamesFormat,
      // Async rule runs on submit (validate-on="submit") to check the URLs are not already
      // present on the same list. Skipped when the format is invalid — reported above.
      async (values: string[]) => {
        if (validateNamesFormat(values) !== true) return true;
        const isUnique = await checkSwgUrlsUnique(values, isAllowList.value);
        return isUnique ? true : t("validations.urlAlreadyExists");
      },
    ];

    const labelRules = [
      (value: DeviceLabel[]) => {
        if (!value.length) {
          return t(
            `validations.deviceLabelRequired.${props.config.item.allowed ? "allowList" : "blockList"}`
          );
        }
        return true;
      },
    ];

    const handleCommaDelimiterPaste = (newVal: string) => {
      const importedDomains = newVal
        .split(",")
        .filter((domain) => domain)
        .map((v) => v.trim());
      currentInputValue.value = "";
      localValue.value.names = uniq([...localValue.value.names, ...importedDomains]);
    };

    const handleBlur = () => {
      if (!currentInputValue.value) return;
      const hasCommaSeparator = hasSeparator(currentInputValue.value);
      if (hasCommaSeparator) {
        currentInputValue.value = "";
      }
    };

    const matchWildcardDomain = (val: string): boolean => {
      return (
        Patterns.WILDCARD_DOMAIN.test(val) ||
        (isAllowList.value && customDomainDnsItems.value.some((item) => item.domain === val))
      );
    };

    const getChipColor = (domain: string) => {
      return matchWildcardDomain(domain) ? "indigo-faint" : "error";
    };

    const actionNotAllowed = computed(() =>
      isWorkspaceFrozenOrActionRestricted(RolePermissionsScope.PROTECTION, SubscriptionModule.SWG)
    );

    // Set only once validation has fully settled — avoids flashing the alert while the
    // async uniqueness rule is still pending (Vuetify reports `valid: false` in the meantime).
    const showValidationError = ref(false);

    const urlFieldLabel = computed(() => {
      return localValue.value.names.length > 0
        ? t("modals.allowBlockListSwg.urlLabel")
        : t("modals.allowBlockListSwg.urlPlaceholder");
    });

    const labelsFieldLabel = computed(() => {
      return localValue.value.labels.length > 0
        ? t("network.secureWebGateway.labelsDropdownLabel")
        : t("network.secureWebGateway.labelsAutocompletePlaceholder");
    });

    const validate = async () => {
      if (!form.value) return false;
      validationTriggered.value = true;
      showValidationError.value = false;
      const { valid: isValid } = await form.value.validate();
      showValidationError.value = !isValid;
      return isValid;
    };

    return {
      form,
      valid,
      localValue,
      currentInputValue,
      rules,
      handleCommaDelimiterPaste,
      handleBlur,
      getChipColor,
      labels,
      actionNotAllowed,
      labelRules,
      validate,
      showValidationError,
      urlFieldLabel,
      labelsFieldLabel,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-autocomplete.coro-scrollable-combobox {
    .v-text-field__details {
      display: flex !important;
    }
  }
}

.color-preview {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}
</style>
